import type { UseNavigateResult } from '@tanstack/react-router'
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns'
import { uniqueId } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import GradebookIcon from '@/assets/icons/gradebook.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import { type SelectOption } from '@/components/Select/Select'
import Table, { type Column, type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TextWithLabel from '@/components/TextWithLabel/TextWithLabel'
import CoverSettingModal from '@/components/common/CoverSettingModal/CoverSettingModal'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import {
  useColumns,
  type RouteApiWithColumnsAndPagination
} from '@/hooks/useColumns'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import useSemesters from '@/queries/useSemesters'
import { type CoveredOrCancelledLesson } from '@/types/lesson'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { formatDateTime, formatTime } from '@/utils/format-date'
import { getColumnWidth } from '@/utils/get-column-width'
import { getFilterValue } from '@/utils/get-filter-value'

import styles from './CoveredAndCancelledLessonsList.module.scss'
import { TruncateColumn } from './TruncateColumn'
import type {
  LessonsFilters,
  LessonsFiltersKey
} from '../constants/lessons-filters'
import useLessonsDownload, {
  type LessonsDownloadParams
} from '../mutations/useLessonsDownload'
import {
  classesQueryOptions,
  coursesQueryOptions,
  groupsQueryOptions,
  teachersQueryOptions
} from '../queries/coveredAndCancelledLessonFiltersOptions'

type CoveredAndCancelledLessonsListProps = {
  id: string
  actions: TableAction<CoveredOrCancelledLesson>[]
  search: LessonsFilters
  lessons: CoveredOrCancelledLesson[]
  refetchLessonList: () => void
  count: number
  totalCount: number
  isTableLoading: boolean
  onNavigate: UseNavigateResult<
    | '/covered-and-cancelled-lessons/cancelled'
    | '/covered-and-cancelled-lessons/covered'
  >
  coverSettingAction: {
    text: string
    icon: React.ReactNode
  }
  coverCancelButton: {
    text: string
    icon: React.ReactNode
  }
  additionalColumns?: Column<CoveredOrCancelledLesson>[]
  routeApi: RouteApiWithColumnsAndPagination
  caption: string
  exportRequestParams: LessonsDownloadParams
}

const CoveredAndCancelledLessonsList = (
  props: CoveredAndCancelledLessonsListProps
) => {
  const { t } = useTranslation('coveredCancelledLessons')

  const { data: semesters, isLoading: isSemestersLoading } = useSemesters()

  const { isMobile, isExtraLarge } = useScreenResolution()

  const { columns, ...manageColumns } = useColumns<CoveredOrCancelledLesson>(
    [
      ...(props.additionalColumns ? props.additionalColumns : []),
      {
        dataIndex: ['course', 'name'],
        key: 'course',
        label: t('header.course'),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },
      {
        dataIndex: ['group', 'name'],
        key: 'group',
        label: t('header.group'),
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'student_classes',
        label: t('header.class'),
        width: isMobile ? TABLE_CELL_WIDTH.MD : undefined,
        render: (lesson: CoveredOrCancelledLesson) => (
          <TruncateColumn
            list={lesson.studentClasses}
            renderElement={item =>
              item.id ? (
                <LinkText
                  key={item.id}
                  className={styles.truncateElement}
                  to="/students-and-classes/classes/$classId/details"
                  params={{
                    classId: item.id
                  }}
                  truncateElement
                >
                  {item.name}
                </LinkText>
              ) : (
                <p key={uniqueId()} className={styles.truncateElement}>
                  {item.name}
                </p>
              )
            }
          />
        )
      },
      {
        key: 'date',
        width: TABLE_CELL_WIDTH.LG,
        label: t('header.date'),
        render: (lesson: CoveredOrCancelledLesson) => (
          <span>
            {`${formatDateTime(lesson?.startDate)} - ${formatTime(lesson?.endDate)}`}
          </span>
        )
      }
    ],
    { routeApi: props.routeApi }
  )

  const handleClearAll = () => {
    props.onNavigate({
      search: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
    })
  }

  const handleChangeFilter = (
    key: LessonsFiltersKey,
    value?: string | string[] | Date | boolean | SelectOption<string>[]
  ) => {
    props.onNavigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }
  const selectedSemester = semesters?.list.find(
    semester => semester.id === props.search.semesterId
  )

  const isArchivedSemester = selectedSemester?.status === 'archived'

  const isDateInsideSelectedSemester = (date: Date) => {
    if (!selectedSemester) return
    return isWithinInterval(startOfDay(date), {
      start: startOfDay(selectedSemester.startDate),
      end: endOfDay(selectedSemester.endDate)
    })
  }

  const shouldDateBeDisabled = (date: Date) =>
    !isDateInsideSelectedSemester(date)

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemestersLoading,
        options: semesters?.semestersFromCurrentYearOptions || [],
        value: props.search.semesterId,
        onChange: value => {
          props.onNavigate({
            search: {
              semesterId: value,
              page: 1,
              pageSize: DEFAULT_PAGE_SIZE
            }
          })
        },
        semesterList: semesters?.list ?? []
      }
    },
    {
      label: t('label.teacher'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'teacher',
        multiple: true,
        queryOptions: search =>
          teachersQueryOptions({ search, semesterId: props.search.semesterId }),
        value: props.search.teacherIds,
        placeholder: t('label.teacher'),
        onChange: value => handleChangeFilter('teacherIds', value)
      }
    },
    {
      label: t('label.course'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'courseIds',
        multiple: true,
        queryOptions: search =>
          coursesQueryOptions({ search, semesterId: props.search.semesterId }),
        value: props.search.courseIds,
        placeholder: t('label.course'),
        onChange: value => handleChangeFilter('courseIds', value)
      }
    },
    {
      label: t('label.group'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'groupIds',
        multiple: true,
        queryOptions: search =>
          groupsQueryOptions({ search, semesterId: props.search.semesterId }),
        value: props.search.groupIds,
        placeholder: t('label.group'),
        onChange: value => handleChangeFilter('groupIds', value)
      }
    },
    {
      label: t('label.class'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'classIds',
        multiple: true,
        queryOptions: search =>
          classesQueryOptions({ search, semesterId: props.search.semesterId }),
        value: props.search.classIds,
        placeholder: t('label.class'),
        onChange: value => handleChangeFilter('classIds', value)
      }
    },
    {
      label: t('label.date-range'),
      variant: 'date-range',
      size: 'wide',
      filterProps: {
        id: 'range',
        value: {
          from: props.search.dateAfter
            ? new Date(props.search.dateAfter)
            : undefined,
          to: props.search.dateBefore
            ? new Date(props.search.dateBefore)
            : undefined
        },
        disabledDate: shouldDateBeDisabled,
        onChange: value => {
          handleChangeFilter('dateAfter', value?.from)
          handleChangeFilter('dateBefore', value?.to)
        },
        defaultMonth: selectedSemester?.startDate
      }
    },
    {
      label: t('label.show-past-data'),
      variant: 'checkbox',
      size: 'flexible',
      filterProps: {
        id: 'show-past-data',
        value: props.search.showPastData,
        label: t('label.show-past-data'),
        onChange: value => handleChangeFilter('showPastData', value)
      }
    }
  ]

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const [currentLesson, setCurrentLesson] = useState<CoveredOrCancelledLesson>()
  const [isCoverSettingsModalOpen, setIsCoverSettingsModalOpen] =
    useState(false)

  const actions: TableAction<CoveredOrCancelledLesson>[] = !isArchivedSemester
    ? [
        {
          icon: <GradebookIcon />,
          onClick: row => {
            if (!props.search.semesterId) return

            props.onNavigate({
              to: '/timetable/$courseId/$groupId/grades',
              params: {
                courseId: row.course.id,
                groupId: row.group.id
              },
              search: {
                semesterId: props.search.semesterId
              }
            })
          },
          text: t('button.go-to-gradebook')
        },
        {
          ...props.coverSettingAction,
          onClick: lesson => {
            setCurrentLesson(lesson)
            setIsCoverSettingsModalOpen(true)
          }
        },
        ...props.actions
      ]
    : []

  const pagination = usePagination(props.routeApi)

  const download = useLessonsDownload({
    ...props.exportRequestParams,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />
            <Button
              className={styles.addUserButton}
              asLink
              icon={props.coverCancelButton.icon}
              to="/covered-and-cancelled-lessons/add-cover-cancel"
              disabled={isArchivedSemester}
            >
              {props.coverCancelButton.text}
            </Button>
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        id={props.id}
        actions={actions}
        isLoading={props.isTableLoading}
        data={props.lessons}
        columns={columns}
        count={props.count}
        totalCount={props.totalCount}
        {...pagination}
        expandedRowRender={record => (
          <TextWithLabel
            label={t('label.comment')}
            variant="vertical"
            text={record.comment}
          />
        )}
        caption={props.caption}
      />

      {currentLesson ? (
        <CoverSettingModal
          open={isCoverSettingsModalOpen}
          onOpenChange={setIsCoverSettingsModalOpen}
          courseName={currentLesson.course.name}
          groupName={currentLesson.group.name}
          lesson={currentLesson}
          onCoverChange={() => {
            props.refetchLessonList()
            setCurrentLesson(undefined)
          }}
        />
      ) : null}
    </>
  )
}

export default CoveredAndCancelledLessonsList
